import { Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";

import {
  DisableTexterSendMutation,
  DisableTexterSendMutationVariables,
} from "~frontend/__generated__/graphql";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import {
  DISABLE_TEXTER_SEND,
  GET_CAMPAIGN_FOR_VIEW,
} from "~frontend/graphql/campaigns";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { CampaignForView } from "../types";
import { SendButtons } from "./SendButtons";

export default function InitialSendReady({
  campaign,
  hasActBlueIntegration,
  revealSendStatus,
}: {
  campaign: CampaignForView;
  hasActBlueIntegration: boolean;
  revealSendStatus: () => void;
}): ReactElement {
  const confirm = useConfirm();
  const [canceling, setCanceling] = useState(false);

  const [disableTexterSend] = useMutation<
    DisableTexterSendMutation,
    DisableTexterSendMutationVariables
  >(DISABLE_TEXTER_SEND, {
    onErrorSnackbarMessage: "Failed to disable texter send",
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
  });

  async function handleDisableTexterSend() {
    if (canceling) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Pause Campaign Sending",
      description: "Are you sure you want to pause campaign sending?",
      confirmationText: "Pause Sending",
      cancellationText: "No",
    });
    if (!confirmed) {
      return;
    }

    setCanceling(true);
    await disableTexterSend();
    setCanceling(false);
  }

  return (
    <>
      <SendButtons
        campaign={campaign}
        hasActBlueIntegration={hasActBlueIntegration}
        revealSendStatus={revealSendStatus}
      />
      {campaign.texterSendEnabled && (
        <Typography variant="body2" sx={{ color: palette.darkGray }}>
          Texter Send is turned on. Texters are able to send batches of initial
          texts, {campaign.texterSendBatchSize!.toLocaleString()} at a time
          until all initials are sent. If you want to bulk send the remainder of
          the initials, you must first{" "}
          {canceling ? (
            <>
              <Loading size={10} style={{ display: "inline" }} /> pause campaign
              sending
            </>
          ) : (
            <LinkButton onClick={handleDisableTexterSend}>
              pause campaign sending
            </LinkButton>
          )}
          .
        </Typography>
      )}
    </>
  );
}
